import { List } from "immutable";
import { createSelector } from "reselect";

import { State } from "common/types/redux";

import { PATH_TO_CLASS_ENROLLMENTS_STATE } from "../consts";
import ClassEnrollment from "../models/ClassEnrollment";
import { getSortedTeacherNames } from "./common";

export const getClassEnrollmentsSlice = (state: State) =>
  state.get(PATH_TO_CLASS_ENROLLMENTS_STATE);

export const getEnrolledClasses = (state: any): List<ClassEnrollment> =>
  // @ts-ignore
  getClassEnrollmentsSlice(state).get("enrollments");

export const getActiveEnrolledClasses = (state: any): List<ClassEnrollment> =>
  getEnrolledClasses(state).filter(
    enrolledClass =>
      enrolledClass.teachers.count() !== 0 &&
      (enrolledClass.archived === undefined || !enrolledClass.archived)
  );

export const getClassEnrollmentDropdownOption = (
  enrollment: ClassEnrollment
) => {
  return {
    value: enrollment.classId,
    label: `${enrollment.className} (${enrollment.teacherName})`
  };
};

export const makeGetEnrolledClassDropdownOptions = () =>
  createSelector(getEnrolledClasses, enrollments =>
    enrollments.map(enrollment => {
      return getClassEnrollmentDropdownOption(enrollment);
    }, List<{ value: string; label: string }>())
  );

export const makeGetClassDropdownOptionsForCourse = (courseId: string) =>
  createSelector(getEnrolledClasses, enrollments =>
    enrollments.reduce((acc, enrollment) => {
      const courseIsInClass = enrollment.courseIds.includes(courseId);

      return courseIsInClass
        ? acc.push(getClassEnrollmentDropdownOption(enrollment))
        : acc;
    }, List<{ value: string; label: string }>())
  );

export const getEnrolledClassesWithCourse = (courseId: string) =>
  createSelector(getEnrolledClasses, enrollments =>
    enrollments.filter(enrollment => enrollment.courseIds?.includes(courseId))
  );

export const hasEnrollmentsForCourse = (courseId: string) =>
  createSelector(getEnrolledClasses, enrollments =>
    enrollments.some(enrollment => enrollment.courseIds.includes(courseId))
  );

export const getNumberOfEnrolledClasses = (state: any): number =>
  getEnrolledClasses(state).size;

export const hasNoEnrolledClasses = (state: any): boolean =>
  getNumberOfEnrolledClasses(state) === 0;

export const getEnrolledClassIds: (arg0: any) => List<string> = createSelector(
  getEnrolledClasses,
  enrolledClasses => enrolledClasses.map((e: any) => e.classId)
);

export const getUserIsEnrolledInClass = (state: any) =>
  !!getNumberOfEnrolledClasses(state);

export const isEnrolledInClass = (state: any, classId: string): boolean =>
  getEnrolledClassIds(state).includes(classId);

export const getClassEnrollment = (
  state: any,
  classId: string
): ClassEnrollment | null | undefined =>
  getEnrolledClasses(state).find(
    (enrollment: ClassEnrollment) => enrollment.classId === classId
  );

export const getClassEnrollmentsByUserId = (
  state: any,
  userId: string
): List<ClassEnrollment> | null | undefined =>
  getEnrolledClasses(state).filter(
    (enrollment: ClassEnrollment) => enrollment.userId === userId
  );

export const getTeacherNameForEnrollment = (
  state: any,
  classId: string
): string | undefined => {
  const classEnrollment = getClassEnrollment(state, classId);
  return classEnrollment?.teacherName;
};

export function makeGetSortedTeacherNamesForEnrollment(classId: string) {
  return createSelector(
    (state: any) => getEnrollmentTeachers(state, classId),
    teachers => getSortedTeacherNames(teachers!.toList())
  );
}

export const getClassNameForEnrollment = (state: any, classId: string) => {
  const enrollment = getClassEnrollment(state, classId);
  return enrollment ? enrollment.className : null;
};

export const getIsClassManagedForEnrollment = (state: any, classId: string) => {
  const enrollment = getClassEnrollment(state, classId);
  return enrollment ? enrollment.isClassManaged : null;
};

export const getTimeEnrolledInClass = (state: any, classId: string) => {
  const enrollment = getClassEnrollment(state, classId);
  return enrollment ? enrollment.timeCreated : null;
};

export const getEnrollmentCourseIds = (state: any, classId: string) => {
  const enrollment = getClassEnrollment(state, classId);
  return enrollment ? enrollment.courseIds : null;
};

export const getEnrollmentTeachers = (state: any, classId: string) => {
  const enrollment = getClassEnrollment(state, classId);
  return enrollment ? enrollment.teachers : null;
};

export const getEnrollmentPrimaryCourseId = (state: any, classId: string) => {
  const courseIds = getEnrollmentCourseIds(state, classId);
  return courseIds ? courseIds.first() : null;
};

export const getEnrollmentClassUserIds = (state: any, classId: string) => {
  const enrollment = getClassEnrollment(state, classId);
  return enrollment ? enrollment.enrollmentUserIds : null;
};
